/* ==================================================================
   VS | Video Hero — JavaScript
   Vanilla JS, no jQuery, no external libraries
   Handles: YouTube API, video switching, autoplay, mute,
            pause-when-out-of-viewport, lazy load, animations
================================================================== */

(function () {
  'use strict';

  /**
   * Global YouTube IFrame API loader.
   * Ensures the script is only injected once per page,
   * and resolves a shared promise once the API is ready.
   */
  var YouTubeAPI = (function () {
    var promise = null;

    function load() {
      if (promise) {
        return promise;
      }

      promise = new Promise(function (resolve, reject) {
        if (window.YT && window.YT.Player) {
          resolve(window.YT);
          return;
        }

        try {
          var existingCallback = window.onYouTubeIframeAPIReady;

          window.onYouTubeIframeAPIReady = function () {
            if (typeof existingCallback === 'function') {
              existingCallback();
            }
            resolve(window.YT);
          };

          var tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          tag.onerror = function () {
            reject(new Error('VS Video Hero: Failed to load YouTube IFrame API.'));
          };
          document.head.appendChild(tag);
        } catch (error) {
          reject(error);
        }
      });

      return promise;
    }

    return { load: load };
  })();

  /**
   * Controller class for a single VS Video Hero section instance.
   */
  function VsVideoHero(section) {
    this.section = section;
    this.wrapper = section.querySelector('[data-vs-video-wrapper]');
    this.contentEl = section.querySelector('[data-vs-animate]');

    this.videoSource = section.getAttribute('data-video-source') || 'shopify';
    this.autoplay = section.getAttribute('data-autoplay') === 'true';
    this.loop = section.getAttribute('data-loop') === 'true';
    this.muted = section.getAttribute('data-muted') === 'true';
    this.pauseOutOfView = section.getAttribute('data-pause-out-of-view') === 'true';
    this.lazyLoad = section.getAttribute('data-lazy-load') === 'true';

    this.playBtn = section.querySelector('[data-vs-play-toggle]');
    this.muteBtn = section.querySelector('[data-vs-mute-toggle]');

    this.nativeVideoEl = null;
    this.ytPlayer = null;
    this.ytReady = false;
    this.isPlaying = false;
    this.hasStartedLoading = false;
    this.reducedMotion = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

    this.init();
  }

  VsVideoHero.prototype.init = function () {
    this.bindControls();
    this.observeVisibility();
    this.observeAnimation();

    // If lazy loading is disabled, start immediately.
    if (!this.lazyLoad) {
      this.loadVideo();
    }
  };

  /* ----------------------------------------------------------------
     Visibility handling — lazy load + pause when out of view
  ---------------------------------------------------------------- */
  VsVideoHero.prototype.observeVisibility = function () {
    var self = this;

    if (!('IntersectionObserver' in window)) {
      // Fallback: no IO support, just load immediately.
      self.loadVideo();
      return;
    }

    this.visibilityObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (!self.hasStartedLoading) {
              self.loadVideo();
            } else {
              self.playVideo();
            }
          } else if (self.pauseOutOfView) {
            self.pauseVideo();
          }
        });
      },
      { root: null, threshold: 0.25 }
    );

    this.visibilityObserver.observe(this.wrapper);
  };

  /* ----------------------------------------------------------------
     Content animation on scroll into view
  ---------------------------------------------------------------- */
  VsVideoHero.prototype.observeAnimation = function () {
    var self = this;

    if (!this.contentEl) {
      return;
    }

    if (!('IntersectionObserver' in window) || this.reducedMotion) {
      this.contentEl.classList.add('vs-is-visible');
      return;
    }

    var animObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self.contentEl.classList.add('vs-is-visible');
            animObserver.unobserve(entry.target);
          }
        });
      },
      { root: null, threshold: 0.2 }
    );

    animObserver.observe(this.contentEl);
  };

  /* ----------------------------------------------------------------
     Video loading — dispatches to the correct source handler
  ---------------------------------------------------------------- */
  VsVideoHero.prototype.loadVideo = function () {
    if (this.hasStartedLoading) {
      return;
    }
    this.hasStartedLoading = true;

    if (this.videoSource === 'youtube') {
      this.setupYouTube();
    } else {
      this.setupNativeVideo();
    }
  };

  /* ----------------------------------------------------------------
     Shopify hosted (native <video>) handling
  ---------------------------------------------------------------- */
  VsVideoHero.prototype.setupNativeVideo = function () {
    var self = this;
    this.nativeVideoEl = this.section.querySelector('.vs-video__el');

    if (!this.nativeVideoEl) {
      return;
    }

    try {
      this.nativeVideoEl.muted = this.muted;
      this.nativeVideoEl.loop = this.loop;
      this.nativeVideoEl.playsInline = true;
      this.nativeVideoEl.setAttribute('playsinline', '');

      if (this.autoplay) {
        var playPromise = this.nativeVideoEl.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(function () {
              self.isPlaying = true;
              self.updatePlayButton();
            })
            .catch(function () {
              // Autoplay was blocked by the browser — leave paused state.
              self.isPlaying = false;
              self.updatePlayButton();
            });
        } else {
          self.isPlaying = true;
          self.updatePlayButton();
        }
      }

      this.nativeVideoEl.addEventListener('play', function () {
        self.isPlaying = true;
        self.updatePlayButton();
      });

      this.nativeVideoEl.addEventListener('pause', function () {
        self.isPlaying = false;
        self.updatePlayButton();
      });

      this.nativeVideoEl.addEventListener('error', function () {
        self.handleVideoError('Shopify hosted video failed to load.');
      });
    } catch (error) {
      this.handleVideoError(error.message);
    }
  };

  /* ----------------------------------------------------------------
     YouTube handling
  ---------------------------------------------------------------- */
  VsVideoHero.prototype.setupYouTube = function () {
    var self = this;
    var container = this.section.querySelector('[data-vs-youtube-container]');

    if (!container) {
      return;
    }

    var videoId = container.getAttribute('data-youtube-id');
    var playerEl = container.querySelector('[data-vs-youtube-player]');

    if (!videoId || !playerEl) {
      this.handleVideoError('Missing or invalid YouTube video ID.');
      return;
    }

    YouTubeAPI.load()
      .then(function (YT) {
        self.ytPlayer = new YT.Player(playerEl, {
          videoId: videoId,
          playerVars: {
            autoplay: self.autoplay ? 1 : 0,
            mute: self.muted ? 1 : 0,
            loop: self.loop ? 1 : 0,
            playlist: self.loop ? videoId : undefined,
            controls: 0,
            showinfo: 0,
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
            fs: 0,
            disablekb: 1,
            iv_load_policy: 3
          },
          events: {
            onReady: function (event) {
              self.ytReady = true;
              if (self.muted) {
                event.target.mute();
              }
              if (self.autoplay) {
                event.target.playVideo();
              }
              self.updateMuteButton();
            },
            onStateChange: function (event) {
              if (window.YT && event.data === window.YT.PlayerState.PLAYING) {
                self.isPlaying = true;
                self.updatePlayButton();
              } else if (
                window.YT &&
                (event.data === window.YT.PlayerState.PAUSED ||
                  event.data === window.YT.PlayerState.ENDED)
              ) {
                self.isPlaying = false;
                self.updatePlayButton();
              }
            },
            onError: function () {
              self.handleVideoError('YouTube video failed to load.');
            }
          }
        });
      })
      .catch(function (error) {
        self.handleVideoError(error.message);
      });
  };

  /* ----------------------------------------------------------------
     Playback controls
  ---------------------------------------------------------------- */
  VsVideoHero.prototype.playVideo = function () {
    if (this.videoSource === 'youtube') {
      if (this.ytPlayer && this.ytReady && typeof this.ytPlayer.playVideo === 'function') {
        this.ytPlayer.playVideo();
      }
    } else if (this.nativeVideoEl) {
      var playPromise = this.nativeVideoEl.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(function () {
          /* Ignore blocked autoplay on resume */
        });
      }
    }
  };

  VsVideoHero.prototype.pauseVideo = function () {
    if (this.videoSource === 'youtube') {
      if (this.ytPlayer && this.ytReady && typeof this.ytPlayer.pauseVideo === 'function') {
        this.ytPlayer.pauseVideo();
      }
    } else if (this.nativeVideoEl) {
      this.nativeVideoEl.pause();
    }
  };

  VsVideoHero.prototype.togglePlay = function () {
    if (this.isPlaying) {
      this.pauseVideo();
    } else {
      this.playVideo();
    }
  };

  VsVideoHero.prototype.toggleMute = function () {
    this.muted = !this.muted;

    if (this.videoSource === 'youtube') {
      if (this.ytPlayer && this.ytReady) {
        if (this.muted) {
          this.ytPlayer.mute();
        } else {
          this.ytPlayer.unMute();
        }
      }
    } else if (this.nativeVideoEl) {
      this.nativeVideoEl.muted = this.muted;
    }

    this.updateMuteButton();
  };

  /* ----------------------------------------------------------------
     UI state syncing
  ---------------------------------------------------------------- */
  VsVideoHero.prototype.updatePlayButton = function () {
    if (this.playBtn) {
      this.playBtn.setAttribute('aria-pressed', this.isPlaying ? 'true' : 'false');
    }
  };

  VsVideoHero.prototype.updateMuteButton = function () {
    if (this.muteBtn) {
      this.muteBtn.setAttribute('aria-pressed', this.muted ? 'true' : 'false');
    }
  };

  /* ----------------------------------------------------------------
     Controls binding (keyboard + click accessible)
  ---------------------------------------------------------------- */
  VsVideoHero.prototype.bindControls = function () {
    var self = this;

    if (this.playBtn) {
      this.playBtn.addEventListener('click', function () {
        self.togglePlay();
      });
    }

    if (this.muteBtn) {
      this.muteBtn.addEventListener('click', function () {
        self.toggleMute();
      });
    }
  };

  /* ----------------------------------------------------------------
     Error handling
  ---------------------------------------------------------------- */
  VsVideoHero.prototype.handleVideoError = function (message) {
    if (window.console && typeof window.console.warn === 'function') {
      window.console.warn('[VS Video Hero]', message);
    }

    var wrapper = this.wrapper;
    if (wrapper && !wrapper.querySelector('.vs-video--error-fallback')) {
      var fallback = document.createElement('div');
      fallback.className = 'vs-video vs-video--error-fallback';
      fallback.setAttribute('role', 'status');
      wrapper.appendChild(fallback);
    }
  };

  /* ----------------------------------------------------------------
     Cleanup for Shopify theme editor (section reload / removal)
  ---------------------------------------------------------------- */
  VsVideoHero.prototype.destroy = function () {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }
    if (this.ytPlayer && typeof this.ytPlayer.destroy === 'function') {
      try {
        this.ytPlayer.destroy();
      } catch (e) {
        /* no-op */
      }
    }
  };

  /* ----------------------------------------------------------------
     Instance registry + initialization
  ---------------------------------------------------------------- */
  var instances = new WeakMap();

  function initSection(section) {
    if (!section || instances.has(section)) {
      return;
    }
    instances.set(section, new VsVideoHero(section));
  }

  function destroySection(section) {
    var instance = instances.get(section);
    if (instance) {
      instance.destroy();
      instances.delete(section);
    }
  }

  function initAll(root) {
    var scope = root || document;
    var sections = scope.querySelectorAll('[data-vs-video-hero]');
    sections.forEach(initSection);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    initAll(document);
  }

  /* Shopify theme editor support: re-init on section load, cleanup on unload */
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-vs-video-hero]');
    if (section) {
      initSection(section);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target.querySelector('[data-vs-video-hero]');
    if (section) {
      destroySection(section);
    }
  });

  document.addEventListener('shopify:section:select', function (event) {
    var section = event.target.querySelector('[data-vs-video-hero]');
    var instance = section && instances.get(section);
    if (instance) {
      instance.playVideo();
    }
  });

  document.addEventListener('shopify:section:deselect', function (event) {
    var section = event.target.querySelector('[data-vs-video-hero]');
    var instance = section && instances.get(section);
    if (instance && instance.pauseOutOfView) {
      instance.pauseVideo();
    }
  });
})();